// @ts-ignore
import moment from 'moment';
import * as R from 'ramda';
import { pluralWithNumber } from 'system/libs/plural';

const DAYS = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

const getHumanReadableTexts = (
    exp: string,
    params: { utcOffset?: number } = {},
) => {
    let resultDays: string | undefined;
    let resultTime: string | undefined;

    const [minute, hour, day, month, weekdaysStr] = exp.split(' ');

    if (hour.startsWith('*/')) {
        resultTime = _getRepeatingHourText(Number(hour.slice(2)));
    } else if (
        _isMissed(hour, day, month, weekdaysStr) &&
        _isProvided(minute)
    ) {
        resultTime = 'Каждый час';
    } else if (_isProvided(minute, hour) && _isMissed(day, month)) {
        const localVars = _getSafeSortedLocalisedVars({
            minute,
            hour,
            weekdaysStr,
            utcOffset: params.utcOffset,
        });

        resultTime = _formatTime(localVars.hour, localVars.minute);

        const weekdays = localVars.weekdays;

        resultDays = _getWeekdaysStr(weekdays);
    }

    return { days: resultDays, time: resultTime };
};

const _getRepeatingHourText = (hour: number) => {
    if (hour === 1) {
        return 'Каждый час';
    }
    return `Каждые ${pluralWithNumber(hour, ['час', 'часа', 'часов'], ' ')}`;
};

const _getRepeatingHour = (hour: string) => {
    hour = hour.slice(2); // remove "*/"
    if (hour === '1') {
        return 'hourly';
    }
    return `each_${hour}_hours`;
};

const _getSafeSortedLocalisedVars = ({
    minute,
    hour,
    weekdaysStr,
    utcOffset,
}: {
    weekdaysStr: string;
    hour: string;
    minute: string;
    utcOffset?: number;
}) => {
    if (_isMissed(weekdaysStr)) {
        weekdaysStr = '0,1,2,3,4,5,6';
    }

    const getLocalMoment = (weekday: string) => {
        const instance = moment()
            .utc()
            .weekday(+weekday)
            .hour(+hour)
            .minute(+minute)
            .local();
        if (utcOffset) {
            instance.utcOffset(utcOffset);
        }
        return instance;
    };

    const weekdays = weekdaysStr
        .split(',')
        .map((weekday) => String(getLocalMoment(weekday).weekday()))
        .sort();

    const firstDay = weekdays.shift();
    if (firstDay === '0') {
        weekdays.push(firstDay);
    } else {
        weekdays.unshift(firstDay as string);
    }

    const localMoment = getLocalMoment(weekdays[0]);
    hour = String(localMoment.hour());
    minute = String(localMoment.minute());

    return {
        hour,
        minute,
        weekdays,
    };
};

const _getWeekdaysStr = (weekdays: string[]) => {
    switch (true) {
        case weekdays.length === 7:
            return 'Раз в сутки';

        case R.equals(weekdays, ['1', '2', '3', '4', '5']):
            return 'Будние дни';

        case R.equals(weekdays, ['6', '0']):
            return 'Выходные дни';

        default:
            return weekdays.map((dayNumber) => DAYS[dayNumber]).join(', ');
    }
};

export type TextFromCronExp = ReturnType<typeof getHumanReadableTexts>;

const _isProvided = (...items: string[]) => items.every((i) => i !== '*');
const _isMissed = (...items: string[]) => items.every((i) => i === '*');

const _formatTime = (hour: string, minute: string) =>
    `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;

const concatHumanReadableTexts = (result: TextFromCronExp) => {
    const { days, time } = result;
    return [days, time].filter(R.identity).join(' в ');
};

const getDatabasesScheduleParams = (exp: string) => {
    let backupFrequency: string | undefined;

    const [minute, hour, day, month, weekdaysStr] = exp.split(' ');
    let localHour, localMinute, localWeekdays;

    if (hour.startsWith('*/')) {
        backupFrequency = _getRepeatingHour(hour);
    } else if (
        _isMissed(hour, day, month, weekdaysStr) &&
        _isProvided(minute)
    ) {
        backupFrequency = 'hourly';
    } else if (_isProvided(minute, hour) && _isMissed(day, month)) {
        const localVars = _getSafeSortedLocalisedVars({
            hour,
            minute,
            weekdaysStr,
        });
        localHour = localVars.hour;
        localMinute = localVars.minute;
        localWeekdays = localVars.weekdays;
        backupFrequency = 'daily';
    }

    return {
        backupFrequency,
        hour: localHour,
        minute: localMinute,
        weekdays: localWeekdays,
    };
};

export const CronExpHelper = {
    getHumanReadableTexts,
    concatHumanReadableTexts,
    getDatabasesScheduleParams,
};
